<template>
    <div>
        <AlertDismiss ref="alert" />
        <b-overlay :show="processing" class="overlay">
            <div class="row justify-content-center">
                <div class="col-auto input-group mb-2">
                    <span class="input-group-text"><b class="ms-2">{{ roleLabel }}</b></span>
                    <input type="text"
                        class="form-control"
                        placeholder="Buscar permisos..."
                        v-model="searchQuery"
                    >
                    <span class="input-group-text">
                        <b-icon icon="search"></b-icon>
                    </span>
                </div>               
                 <div class="col-6">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <small> <b>Permisos disponibles</b> </small>
                        <div v-b-tooltip.hover title="Asignar permisos seleccionados">
                            <button class="btn btn-outline-success btn-sm py-0"
                                @click="moveSelectedToAssigned"
                                :disabled="!assignSelectedEnabled">
                                <b-icon icon="chevron-right"></b-icon>
                            </button>
                        </div>
                    </div>
                    <div class="permission-list list-group" @drop="onDrop($event, 'unassigned')" @dragover.prevent>
                        <div v-if="emptyUnassignedPermissions" class="drag-placeholder text-center">
                            <b-icon icon="arrows-move" class="drag-icon"></b-icon>
                            <div>Arrastre aquí los permisos que desee quitar</div>
                        </div>

                        <div v-for="permission in filteredUnassignedPermissions" :key="permission.id"
                            class="permission-item list-group-item"
                            :class="unassignedClass(permission)"
                            draggable="true"
                            @dragstart="onDragStart($event, permission, 'unassigned')"
                            @click="toggleSelectUnassigned(permission)">
                            {{ permission.description.trim() }}
                        </div>
                    </div>
                </div>

                <div class="col-6">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <div v-b-tooltip.hover title="Quitar permisos seleccionados">
                            <button class="btn btn-outline-danger btn-sm py-0"
                                @click="moveSelectedToUnassigned"
                                :disabled="!unassignSelectedEnabled">
                                <b-icon icon="chevron-left"></b-icon>
                            </button>
                        </div>
                        <small> <b>Permisos asignados:</b> </small>
                    </div>
                    <div class="permission-list list-group" @drop="onDrop($event, 'assigned')" @dragover.prevent>
                        <div v-if="emptyAssignedPermissions" class="drag-placeholder text-center">
                            <b-icon icon="arrows-move" class="drag-icon"></b-icon>
                            <div>Arrastre aquí los permisos que desee asignar</div>
                        </div>
                        <div v-for="permission in filteredAssignedPermissions" :key="permission.id"
                            class="permission-item list-group-item"
                            :class="assignedClass(permission)"
                            draggable="true"
                            @dragstart="onDragStart($event, permission, 'assigned')"
                            @click="toggleSelectAssigned(permission)">
                            {{ permission.description.trim() }}
                        </div>
                    </div>
                </div>
            </div>

        </b-overlay>
    </div>
</template>
<script>
import AlertDismiss from '@/components/shared/Alert-dismiss.vue';
import { EventNames } from '@/plugins/enums';
import { mapActions, mapGetters } from 'vuex';

export default {
    name: 'SettingModifyPermission',

    components: {
        AlertDismiss,
    },

    props: {
        roleId: String,
        roleLabel: String
    },

    data() {
        return {
            assignedPermissions: [],
            unassignedPermissions: [],
            searchQuery: '',
            draggedItem: null,
            sourceList: null,
            selectedAssigned: [],
            selectedUnassigned: [],
            processing: true
        }
    },

    computed: {
        ...mapGetters(["getRole"]),
        filteredAssignedPermissions() {
            return this.assignedPermissions.filter(permission =>
                permission.name.toLowerCase().includes(this.searchQuery.toLowerCase())
            )
        },

        filteredUnassignedPermissions() {
            return this.unassignedPermissions.filter(permission =>
                permission.name.toLowerCase().includes(this.searchQuery.toLowerCase())
            )
        },

        assignSelectedEnabled() {
            return this.selectedUnassigned.length > 0
        },

        unassignSelectedEnabled() {
            return this.selectedAssigned.length > 0
        },

        emptyAssignedPermissions() {
            return this.assignedPermissions.length === 0
        },

        emptyUnassignedPermissions() {
            return this.unassignedPermissions.length === 0
        },
    },

    mounted() {
        this.getAllAssignedPermissions();
        this.getAllUnassignedPermissions();
    },

    methods: {
        ...mapActions([
            "getPermissions",
            "getNonPermissionForRole",
            "addPermissionFromRole",
            "removePermissionForRole"
        ]),


        onDragStart(event, item, sourceList) {
            this.draggedItem = item
            this.sourceList = sourceList
            event.dataTransfer.effectAllowed = 'move'
        },

        async onDrop(event, targetList) {
            event.preventDefault()

            if (this.sourceList === targetList) return

            if (targetList === 'assigned') {
                await this.assignPermissions([this.draggedItem])
            } else {
                await this.unassignPermissions([this.draggedItem])
            }


            this.draggedItem = null
            this.sourceList = null
        },

        toggleSelectAssigned(permission) {
            const index = this.selectedAssigned.indexOf(permission)
            if (index === -1) {
                this.selectedAssigned.push(permission)
            } else {
                this.selectedAssigned.splice(index, 1)
            }
        },

        toggleSelectUnassigned(permission) {
            const index = this.selectedUnassigned.indexOf(permission)
            if (index === -1) {
                this.selectedUnassigned.push(permission)
            } else {
                this.selectedUnassigned.splice(index, 1)
            }
        },

        async moveSelectedToAssigned() {
            await this.assignPermissions(this.selectedUnassigned)
            this.selectedUnassigned = []
        },

        async moveSelectedToUnassigned() {
            await this.unassignPermissions(this.selectedAssigned)
            this.selectedAssigned = []
        },

        async assignPermissions(permissions) {
            this.processing = true
            for (let permission of permissions) {
                let result = await this.addPermissionFromRole({ permissionId: permission.id, roleId: this.roleId })
                if (result.isError) {
                    this.processing = false
                    this.$refs.alert.showAlert(result.message, 'warning');
                    return;
                }
            }
            this.assignedPermissions.push(...permissions)
            this.assignedPermissions.sort((a, b) => a.description.localeCompare(b.description))
            this.unassignedPermissions = this.unassignedPermissions.filter(p => !permissions.includes(p))
            this.processing = false
            this.successEvents()
        },

        async unassignPermissions(permissions) {
            this.processing = true
            for (let permission of permissions) {
                let result = await this.removePermissionForRole({ permissionId: permission.id, roleId: this.roleId });
                if (result.isError) {
                    this.processing = false
                    this.$refs.alert.showAlert(result.message, 'warning');
                }
            }
            this.unassignedPermissions.push(...permissions)
            this.unassignedPermissions.sort((a, b) => a.description.localeCompare(b.description))
            this.assignedPermissions = this.assignedPermissions.filter(p => !permissions.includes(p))
            this.processing = false
            this.successEvents()
        },

        async getAllAssignedPermissions() {
            this.processing = true
            let result = await this.getPermissions({ rolId: this.roleId });

            if (result.isError) {
                this.$refs.alert.showAlert(result.message);
                this.processing = false
                return;
            }

            this.assignedPermissions = result.data
                .sort((a, b) => a.description.localeCompare(b.description))
            this.processing = false
        },

        async getAllUnassignedPermissions() {
            this.processing = true
            let result = await this.getNonPermissionForRole({ rolId: this.roleId });

            if (result.isError) {
                this.$refs.alert.showAlert(result.message);
                this.processing = false
                return;
            }

            this.unassignedPermissions = result.data
                .sort((a, b) => a.description.localeCompare(b.description))
            this.processing = false
        },

        async addPermission(permission) {
            let result = await this.addPermissionFromRole({
                permissionId: permission.id,
                roleId: this.roleId
            });

            if (result.isError) {
                this.$refs.alertDismiss.showAlert(result.message);
                return false;
            }

            return true
        },

        async removePermission(permission) {
            let result = await this.removePermissionForRole({
                permissionId: permission.id,
                roleId: this.roleId
            });

            if (result.isError) {
                this.$refs.alert.showAlert(result.message);
                return false;
            }
            return true
        },

        showSuccessAlert() {
            let sameRole = this.getRole === this.roleLabel;

            let msg
            if (sameRole) {
                msg = 'Permisos modificados correctamente. Por favor inicie sesión nuevamente para que los cambios surtan efecto.'
            } else {
                msg = 'Permisos modificados correctamente.'
            }
            this.$refs.alert.showAlert(msg, 'success');
        },

        modifiedPermissionEvent() {
            this.$eventBus.$emit(EventNames.ModifiedPermission);
        },

        successEvents() {
            this.showSuccessAlert()
            this.modifiedPermissionEvent()
        },

        unassignedClass(permission) {
            return {
                selected: this.selectedUnassigned.includes(permission)
            }
        },

        assignedClass(permission) {
            return {
                selected: this.selectedAssigned.includes(permission)
            }
        },
    }
}
</script>

<style scoped>
.permission-list {
    min-height: 150px;
    max-height: 50vh;
    overflow-y: auto;
    border: 1px dashed #9a9090;
}


.permission-item {
    cursor: pointer;
    padding-top: 0.3rem;
    padding-bottom: 0.3rem;
    font-size: 0.75rem;
}

.permission-item:hover {
    background-color: #f8f9fa;
}

.permission-item.selected {
    background-color: #0b5ed6;
    color: white;
}

.drag-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 150px;
    border-radius: 4px;
    padding: 20px;
}

.drag-icon {
    font-size: 2.5rem;
    margin-bottom: 10px;
    color: #666;
}
</style>